<template>
  <div class="reserve-order-container">
    <el-row :gutter="16">
      <!-- 左侧分类导航：保留原有 type 切换语义与事件，不改任何业务逻辑 -->
      <el-col :xs="24" :sm="24" :md="5" :lg="4">
        <div class="ds-nav-card">
          <div class="ds-nav-card__header">{{ $t('reserveOrder.orderCategory') }}</div>
          <ul class="ds-nav-card__list">
            <li class="ds-nav-card__item"
              :class="{ 'is-active': reserveOrderInfo.conditions.type === '1001' }"
              @click="_changeType('1001')">
              <i class="el-icon-bowl"></i>
              <span>{{ $t('reserveOrder.diningOrder') }}</span>
            </li>
            <li class="ds-nav-card__item"
              :class="{ 'is-active': reserveOrderInfo.conditions.type === '2002' }"
              @click="_changeType('2002')">
              <i class="el-icon-service"></i>
              <span>{{ $t('reserveOrder.serviceOrder') }}</span>
            </li>
          </ul>
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="19" :lg="20">
        <!-- (1) PageHeader 标题 + 副标题 -->
        <ds-page-header :title="$t('reserveOrder.page.title')" :subtitle="$t('reserveOrder.page.subtitle')" />

        <!-- (2) SearchPanel 筛选区：字段顺序与含义保持不变，仅重排栅格并把动作统一到 actions 插槽 -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="reserveOrderInfo.conditions.appointmentTime" type="date"
                value-format="yyyy-MM-dd" :placeholder="$t('reserveOrder.selectAppointmentTime')" class="w-full" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model="reserveOrderInfo.conditions.personName" clearable
                :placeholder="$t('reserveOrder.selectAppointmentPerson')" @keyup.enter.native="_queryReserveOrderPersonMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model="reserveOrderInfo.conditions.personTel" clearable
                :placeholder="$t('reserveOrder.selectAppointmentPhone')" @keyup.enter.native="_queryReserveOrderPersonMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="reserveOrderInfo.conditions.state" :placeholder="$t('reserveOrder.selectStatus')"
                clearable class="w-full">
                <el-option :label="$t('reserveOrder.allStatus')" value="" />
                <el-option :label="$t('reserveOrder.successStatus')" value="S" />
                <el-option :label="$t('reserveOrder.failStatus')" value="F" />
                <el-option :label="$t('reserveOrder.waitAuditStatus')" value="W" />
                <el-option :label="$t('reserveOrder.waitPayStatus')" value="F" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model="reserveOrderInfo.conditions.goodsNameLike" clearable
                :placeholder="$t('reserveOrder.inputName')" @keyup.enter.native="_queryReserveOrderPersonMethod" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="_queryReserveOrderPersonMethod">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="_resetReserveOrderPersonMethod">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="fetchData">
            <template #left>
              <span class="ds-panel__title">{{ currentTypeLabel }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="reserveOrderInfo.orders" :loading="loading" :error="hasError"
            :error-message="errorMessage" :density="density" border
            :empty-description="$t('reserveOrder.page.empty')" @retry="fetchData">
            <el-table-column prop="orderId" :label="$t('reserveOrder.orderId')" align="center" width="180">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.orderId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="goodsName" :label="$t('reserveOrder.goodsName')" align="left" min-width="140"
              show-overflow-tooltip />
            <el-table-column prop="personName" :label="$t('reserveOrder.appointmentPerson')" align="center" width="100" />
            <el-table-column prop="personTel" :label="$t('reserveOrder.appointmentPhone')" align="center" width="130" />
            <el-table-column prop="appointmentTime" :label="$t('reserveOrder.appointmentDate')" align="center" width="120" />
            <!-- 预约时段：保留原有 hours + 数量 展示，追加时段状态 Tag（仅新增展示信息） -->
            <el-table-column :label="$t('reserveOrder.appointmentTime')" align="left" min-width="200">
              <template slot-scope="scope">
                <div v-if="!scope.row.times || scope.row.times.length === 0" class="ds-cell-sub">-</div>
                <div v-for="(time, index) in scope.row.times" :key="index" class="ds-time-item">
                  <span class="ds-time-item__text">
                    {{ time.hours }}{{ $t('reserveOrder.hour') }}
                    <span v-if="scope.row.type === '1001'">({{ time.quantity }}{{ $t('reserveOrder.unit') }})</span>
                  </span>
                  <ds-status-tag v-if="time.state" size="sm" :type="resolveTimeState(time)"
                    :label="resolveTimeStateLabel(time)" />
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('reserveOrder.receivableAmount')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatMoney(scope.row.receivableAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('reserveOrder.receivedAmount')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatMoney(scope.row.receivedAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="payWayName" :label="$t('reserveOrder.payWay')" align="center" width="110">
              <template slot-scope="scope">
                {{ scope.row.payWayName || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('reserveOrder.status')" align="center" width="120">
              <template slot-scope="scope">
                <ds-status-tag :type="resolveOrderState(scope.row)" :label="resolveOrderStateLabel(scope.row)" />
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('reserveOrder.createTime')" align="center" width="160" />
            <el-table-column :label="$t('reserveOrder.remark')" align="left" min-width="140" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.remark || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="110" fixed="right">
              <template slot-scope="scope">
                <el-button v-if="scope.row.state === 'S'" type="text" size="small"
                  @click="_openDeleteReserveOrderPersonModel(scope.row)">
                  {{ $t('reserveOrder.cancelAppointment') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>

    <delete-reserve-order-person ref="deleteReserveOrderPerson" @success="handleSuccess" />
  </div>
</template>

<script>
import { listReserveGoodsOrder } from '@/api/scm/reserveOrderApi'
import DeleteReserveOrderPerson from '@/components/scm/deleteReserveOrderPerson'
import { getCommunityId } from '@/api/community/communityApi'
import { formatMoney } from '@/utils/format'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ReserveOrderList',
  components: {
    DeleteReserveOrderPerson,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      reserveOrderInfo: {
        orders: [],
        conditions: {
          type: '1001',
          goodsNameLike: '',
          personName: '',
          personTel: '',
          appointmentTime: '',
          state: '',
          communityId: ''
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  computed: {
    /**
     * 当前订单类型标题（就餐订单 / 服务订单），只读展示，不影响请求参数。
     */
    currentTypeLabel() {
      return this.reserveOrderInfo.conditions.type === '1001'
        ? this.$t('reserveOrder.diningOrder')
        : this.$t('reserveOrder.serviceOrder')
    }
  },
  created() {
    this.reserveOrderInfo.conditions.communityId = getCommunityId()
    this.fetchData()
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.reserveOrderInfo.conditions,
          page: this.page.current,
          row: this.page.size
        }
        const res = await listReserveGoodsOrder(params)

        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况，
        // 原实现直接取 data 会在此处抛 TypeError，导致筛选后表格仍停留在上一次的数据。
        this.reserveOrderInfo.orders = Array.isArray(res.data) ? res.data : []
        this.page.total = res.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('reserveOrder.page.loadFailed')
        console.error('Failed to load reserve orders:', error)
      } finally {
        this.loading = false
      }
    },
    _openDeleteReserveOrderPersonModel(row) {
      this.$refs.deleteReserveOrderPerson.open(row)
    },
    _queryReserveOrderPersonMethod() {
      this.page.current = 1
      this.fetchData()
    },
    /**
     * 重置筛选：仅清空可筛选字段，保留 order type 与 communityId。
     */
    _resetReserveOrderPersonMethod() {
      this.reserveOrderInfo.conditions.goodsNameLike = ''
      this.reserveOrderInfo.conditions.personName = ''
      this.reserveOrderInfo.conditions.personTel = ''
      this.reserveOrderInfo.conditions.appointmentTime = ''
      this.reserveOrderInfo.conditions.state = ''
      this._queryReserveOrderPersonMethod()
    },
    _changeType(type) {
      this.reserveOrderInfo.conditions.type = type
      this.page.current = 1
      this.fetchData()
    },
    handleSuccess() {
      this.fetchData()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.fetchData()
    },
    /**
     * 金额格式化（只改展示，不改原值）
     */
    formatMoney(value) {
      return formatMoney(value)
    },
    /**
     * 订单状态语义类型：优先按 state 命中预约域字典，未命中时按 stateName 做语义推断。
     */
    resolveOrderState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'reserve',
        text: row.stateName || ''
      }).type
    },
    /**
     * 订单状态文案：优先使用后端字典文案，缺失时回落到本地 i18n。
     */
    resolveOrderStateLabel(row) {
      if (!row) return '-'
      if (row.stateName) return row.stateName
      const key = `reserveOrder.state.${row.state}`
      const text = this.$t(key)
      return text === key ? '-' : text
    },
    /**
     * 预约时段状态语义类型（W 待核销 / C 已核销 / CL 已取消）。
     */
    resolveTimeState(time) {
      if (!time) return 'muted'
      return resolveStatus(time.state, {
        domain: 'reserveTime',
        text: time.stateName || ''
      }).type
    },
    /**
     * 预约时段状态文案。
     */
    resolveTimeStateLabel(time) {
      if (!time) return '-'
      if (time.stateName) return time.stateName
      const key = `reserveOrder.timeState.${time.state}`
      const text = this.$t(key)
      return text === key ? '-' : text
    }
  }
}
</script>

<style lang="scss" scoped>
.reserve-order-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧分类导航卡片 */
.ds-nav-card {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__header {
    padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
    background: var(--ds-gray-100);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: var(--ds-spacing-sm);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: var(--ds-spacing-sm);
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-xs);
    border-radius: var(--ds-radius-sm);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-700);
    cursor: pointer;
    transition: background-color var(--ds-transition-fast),
      color var(--ds-transition-fast);

    &:hover {
      background-color: rgba(var(--ds-primary-rgb), 0.08);
      color: var(--ds-primary);
    }

    &.is-active {
      background-color: var(--ds-primary);
      color: #fff;
      font-weight: 600;
    }
  }
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 表格次要文字 */
.ds-cell-sub {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
}

/* 预约时段条目 */
.ds-time-item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--ds-spacing-xs);

  &__text {
    font-family: var(--ds-font-family-num);
    font-variant-numeric: tabular-nums;
    color: var(--ds-gray-700);
  }
}
</style>
